import { fetchFeature } from "@asurion-core/expert-workspace-feature-flagging";
import { useQuery } from "@tanstack/react-query";

import { env } from "@/config";
import { useAgentAttributesStore } from "@/sdk/agent/store/agent-attributes.store";

import { userCache } from "../auth";

export interface UseFeatureFlagOptions<T> {
    cacheTtl?: number;
    isEnabled?: boolean;
    onResponse?: (flag: T | undefined) => void;
}

export const useFeatureFlag = <T = boolean>(
    featureFlag: string,
    context: Record<string, unknown> = {},
    { cacheTtl = 1800 /* 30 min */, isEnabled = true, onResponse }: UseFeatureFlagOptions<T> = {},
) => {
    // fall-back to identify (sub) if employeeId is undefined
    const employeeId = userCache.employeeId ?? userCache.identity;
    const agentSite = useAgentAttributesStore((state) => state.site);
    const { data, isLoading, isError } = useQuery({
        queryKey: [featureFlag, ...Object.values(context)],
        queryFn: async (): Promise<T | null> => {
            const result = await fetchFeature<T>(
                featureFlag,
                {
                    environment: env.mode,
                    channel: env.channel,
                    employeeId,
                    appName: "expert-workspace",
                    source: "expert-ui",
                    agentSite,
                    ...context,
                },
                cacheTtl,
            );
            if (!result.ok) {
                // Throw so a failed fetch surfaces as `isError` instead of resolving like a flag that is off.
                if (result.reason === "failed") throw result.error;
                // An unconfigured key is not an error: resolving it like an absent value keeps code safe to
                // ship before its flag config is deployed in every environment.
                onResponse?.(undefined);
                return null;
            }
            onResponse?.(result.value);
            // React Query rejects an `undefined` return ("Query data cannot be undefined"), so coalesce a
            // successful-but-empty value to null: consumers still get a falsy value via their
            // `= defaultValue`/nullish handling.
            return result.value ?? null;
        },
        // ky already retries the request; a second retry layer here would multiply load on an outage.
        // `retryOnMount` is separate from `retry`: without it an errored query refetches on every mount.
        retry: false,
        retryOnMount: false,
        refetchOnMount: false,
        enabled: isEnabled,
    });
    // Map the internal `null` sentinel back to `undefined` so the public shape stays `T | undefined`
    // (unchanged for callers); `??` leaves legitimate falsy flag values like `false`/`0`/`""` intact.
    return { data: data ?? undefined, isLoading, isError };
};
